import { PriceDetailPosition, ProductInterface } from '@bytel/product-wall/types';

import { getFundingOrder, getFundingPhone } from '@services/products';

import { SapiPlanType } from '@app-types/api/sapi';
import { ContractType } from '@app-types/contract';
import { DiscountEnumType, DiscountType } from '@app-types/discount';
import { FundingType } from '@app-types/funding';
import {
    MobilePlanDetailWithProvenanceType,
    MobilePlanProvenanceType,
    MobilePlanType,
    PlanType,
} from '@app-types/plan';
import { Product, ProductChildFull, ProductType } from '@app-types/product';
import { GalliumDataStoreApi } from '@app-types/renewal';

import { SapicMapper } from '@helpers/mappers/sapic-mapper';
import { paths } from '@helpers/path';
import { LockedPlanValue } from '@helpers/plan-lock';

import { PlanSubTypeId, PlanTypeId } from '@constants/plan';

const createProductDisplay = (
    product: Product,
    plan?: MobilePlanDetailWithProvenanceType,
    contract?: ContractType,
): Product => {
    if (plan) {
        const isSmartChange = plan.plan?.subTypeId === PlanSubTypeId.SMART_CHANGE;
        const isFunding = product.priceFrom;
        const isPlanFromUrl = plan.origin === MobilePlanProvenanceType.URL;
        const isPlanFromCart = plan.origin === MobilePlanProvenanceType.CART;
        const isPremiumRenewal = contract?.subscription.isPremium ?? false;

        const newProduct: Product = { ...product };

        newProduct.priceFrom = !isPlanFromUrl && ((!isPremiumRenewal && !isPlanFromCart) || isFunding);

        if (isSmartChange) {
            // Thanks to Trilogy only doing client side, we have to hardcode the HTML...
            newProduct.decoration = (
                <span
                    className='icon is-medium is-circled is-main has-background-main-fade'
                    title='Téléphone éligible Smart Change'
                >
                    <i className='tri-smart-change' aria-hidden='true'></i>
                </span>
            );
        }

        if (!product.priceDetail && newProduct.priceFrom) {
            newProduct.priceDetail = {
                position: PriceDetailPosition.BESIDE,
            };
        }

        return newProduct;
    }
    return product;
};

export const getDisplayProducts = (
    products: Product[],
    plan?: MobilePlanDetailWithProvenanceType,
    contract?: ContractType,
): Product[] => {
    return products.map((product) => createProductDisplay(product, plan, contract));
};

export const findProduct = (products: Product[], id: ProductInterface['id']): Product | undefined => {
    return products.find((product) => product.id === id);
};

export const getSimilarProductsLink = ({ type, manufacturerCode }: ProductChildFull, isRenewal: boolean) => {
    if (type === ProductType.PHONE || type === ProductType.PHONE_SIMPLE) {
        return paths.phoneFilter(isRenewal, manufacturerCode);
    }

    return type === ProductType.ACCESSORY ? paths.accessoryWall : '';
};

export const mapApiPlanToMobilePlanType = (
    planSapi: SapiPlanType,
    planSapic?: MobilePlanType,
    phoneGencode?: string,
): MobilePlanType => {
    let fundings: FundingType[] = [];
    if (phoneGencode && planSapic) {
        fundings = getFundingPhone(planSapic, phoneGencode)?.sort(
            (a: FundingType, b: FundingType) => getFundingOrder(a.type) - getFundingOrder(b.type),
        );
    }

    const basePlan: Partial<MobilePlanType> = planSapic ? planSapic : {};
    // @TODO: no mapping here - use repo / mapper
    const detailData: Partial<MobilePlanType> = {
        image: '',
        recommended: false,
        inStock: true,
        gencode: planSapi.gencode,
        name: planSapic?.name ?? planSapi.name,
        typeId: planSapi.type_id as PlanTypeId,
        dataEnvelope: planSapi.data_envelope || '',
        dataSize: planSapi.data_size,
        hasLowPrice: planSapi.low_prices_wall,
        hasDoubleSim: planSapi.double_sim,
        isDarwin: planSapi.is_darwin,
        subTypeId: planSapi.sub_type_id ?? PlanSubTypeId.CLASSIC,
        locked: planSapi.locked as LockedPlanValue,
        smsMms: planSapi.sms_mms,
        internationalCall: planSapi.international_call,
        fromAbroad: planSapi.from_abroad,
        isExcluPro: planSapi.exclu_pro,
        price: planSapi.price,
        maxConnections: planSapi.max_connection,
        detailsDescription: planSapi.catchy_description,
        fundings,
        urlKey: planSapi.url_key,
        commercialRange: planSapi.commercial_range,
        entityId: planSapi.entity_id,
        planFlag: planSapi.plan_flag,
        obligation: planSapi.obligation,
        obligationLabel: SapicMapper.getObligationLabel(planSapi.obligation),
    };

    return { ...basePlan, ...detailData } as MobilePlanType;
};

export const getDiscountValue = (discounts: DiscountType[], type: DiscountEnumType): number => {
    return discounts.reduce((acc, discount) => {
        return discount.type === type ? acc + discount.value : acc;
    }, 0);
};

export const getProductNameByGencode = (gencode: string, products: PlanType[]): string | undefined => {
    const product = products.find((plan) => plan.gencode === gencode);
    return product?.name;
};

export function getProductName(brand?: string, name: string = '') {
    return brand ? `${brand} ${name}` : name;
}

export function getNeededElementGencodes(product: Product | undefined | null): string[] {
    return product?.neededElements?.map((el) => el.gencode) || [];
}

export const addRenewalInfoToPlans = (plans: MobilePlanType[], allPlansGallium: GalliumDataStoreApi) => {
    return plans.map((plan) => {
        const isPlanOwned = allPlansGallium?.opSource.idOffre === plan.gencode;
        const newPlan = allPlansGallium?.simulation?.find((offer) => offer.idOffre === plan.gencode) || {};
        const source = isPlanOwned ? allPlansGallium.opSource : newPlan;

        const renewalDiscounts = source.remiseAvantage;
        const hasRenewalDiscounts = Math.abs(source.tarifMoinsRemise?.montantRemiseTTC ?? 0) > 0;

        const position = isPlanOwned
            ? 0
            : allPlansGallium?.simulation.map((offer) => offer.idOffre).indexOf(plan.gencode) + 1;

        return {
            ...plan,
            isOwned: isPlanOwned,
            position: position,
            renewalDiscounts: renewalDiscounts,
            hasRenewalDiscounts: hasRenewalDiscounts,
        };
    });
};
